import { useState } from 'react';
import Sidebar from '../components/Sidebar';
import { api } from '../hooks/useAuth';

const getApiBaseUrl = () =>
  (api.defaults.baseURL || process.env.REACT_APP_BASE_URL || window.location.origin || '').replace(/\/$/, '');

export default function UploadReels() {
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const videoFiles = files.filter(f => f.type.startsWith('video/'));
    
    if (videoFiles.length === 0) {
      setMessage({ type: 'error', text: 'Please select video files only' });
      return;
    }

    setUploadFiles(videoFiles);
    setMessage({ type: 'info', text: `${videoFiles.length} video(s) selected` });
  };

  const uploadReels = async () => {
    if (uploadFiles.length === 0) {
      setMessage({ type: 'error', text: 'Please select videos to upload' });
      return;
    }

    setUploading(true);
    const successCount = [];
    const failedCount = [];

    for (const file of uploadFiles) {
      try {
        console.log('📤 Uploading:', file.name, 'Size:', (file.size / 1024 / 1024).toFixed(2), 'MB');
        
        const formData = new FormData();
        formData.append('video', file);
        formData.append('caption', file.name.replace(/\.[^/.]+$/, ''));

        const token = localStorage.getItem('vc_token');
        const baseURL = getApiBaseUrl();

        const response = await fetch(`${baseURL}/api/reels/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        const data = await response.json();

        // ── Manual upload disabled by admin ──────────────────────────
        if (response.status === 403) {
          setMessage({ type: 'error', text: '🚫 ' + (data.error || 'Manual upload access has been disabled for your account. Please contact your administrator.') });
          setUploading(false);
          return;
        }

        if (!response.ok) {
          throw new Error(data.error || 'Upload failed');
        }

        console.log('✅ Upload successful:', file.name);
        successCount.push(file.name);
        
      } catch (error) {
        console.error('❌ Upload failed:', file.name, error.message);
        failedCount.push(file.name);
      }
    }

    setUploading(false);
    setUploadFiles([]);

    if (successCount.length > 0) {
      setMessage({ 
        type: 'success', 
        text: `✅ ${successCount.length} reel(s) uploaded successfully!` 
      });
    }

    if (failedCount.length > 0) {
      setMessage({ 
        type: 'error', 
        text: `❌ ${failedCount.length} reel(s) failed to upload` 
      });
    }
  };

  const removeFile = (index) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content">
        <div className="page-header">
          <div>
            <h1 className="page-title">📤 Upload Reels</h1>
            <p className="page-subtitle">Upload videos directly from your computer</p>
          </div>
        </div>

        {message && (
          <div 
            className={`alert alert-${message.type}`} 
            onClick={() => setMessage(null)}
            style={{ cursor: 'pointer', marginBottom: 24 }}
          >
            {message.text}
          </div>
        )}

        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>📁 Upload Videos</h3>
          <p style={{ fontSize: 13, color: 'var(--text-dim)', marginBottom: 20 }}>
            Select video files from your computer to upload
          </p>

          {/* Drop Zone */}
          <div style={{ 
            border: '2px dashed var(--border)', 
            borderRadius: 12, 
            padding: 40,
            textAlign: 'center',
            marginBottom: 16,
            background: 'var(--bg)',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onDragOver={(e) => { 
            e.preventDefault(); 
            e.currentTarget.style.borderColor = 'var(--accent)';
            e.currentTarget.style.background = 'rgba(255, 77, 109, 0.05)';
          }}
          onDragLeave={(e) => { 
            e.currentTarget.style.borderColor = 'var(--border)';
            e.currentTarget.style.background = 'var(--bg)';
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = 'var(--border)';
            e.currentTarget.style.background = 'var(--bg)';
            handleFileSelect({ target: { files: e.dataTransfer.files }});
          }}
          onClick={() => document.getElementById('file-input').click()}
          >
            <div style={{ fontSize: 48, marginBottom: 12 }}>🎬</div>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
              {uploadFiles.length > 0 
                ? `${uploadFiles.length} file(s) selected` 
                : 'Click to select or drag videos here'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-dim)' }}>
              Supports: MP4, MOV, WebM • Max 100MB per file
            </div>
            <input
              id="file-input"
              type="file"
              accept="video/*"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </div>

          {/* Selected Files List */}
          {uploadFiles.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ 
                fontSize: 13, 
                fontWeight: 600, 
                marginBottom: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>Selected Files:</span>
                <button 
                  className="btn btn-ghost btn-sm"
                  onClick={() => setUploadFiles([])}
                >
                  Clear All
                </button>
              </div>
              <div style={{ 
                maxHeight: 200, 
                overflowY: 'auto',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: 8
              }}>
                {uploadFiles.map((file, i) => (
                  <div key={i} style={{ 
                    padding: '10px 12px', 
                    background: 'var(--bg)', 
                    borderRadius: 8,
                    marginBottom: 6,
                    fontSize: 12,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontWeight: 600, 
                        marginBottom: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        📹 {file.name}
                      </div>
                      <div style={{ color: 'var(--text-dim)' }}>
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                    <button 
                      className="btn btn-ghost btn-sm"
                      onClick={() => removeFile(i)}
                      style={{ color: 'var(--danger)' }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Button */}
          <button 
            className="btn btn-primary btn-lg" 
            onClick={uploadReels}
            disabled={uploading || uploadFiles.length === 0}
            style={{ width: '100%' }}
          >
            {uploading 
              ? <><span className="spinner" /> Uploading {uploadFiles.length} file(s)...</> 
              : `📤 Upload ${uploadFiles.length || ''} Reel${uploadFiles.length !== 1 ? 's' : ''}`
            }
          </button>

          {/* Info Box */}
          <div style={{ 
            marginTop: 20,
            padding: '14px 16px', 
            background: 'var(--bg3)', 
            borderRadius: 8,
            fontSize: 12,
            lineHeight: 1.6
          }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>💡 Tips:</div>
            <ul style={{ margin: 0, paddingLeft: 20, color: 'var(--text-dim)' }}>
              <li>Videos will be saved to your Reels library</li>
              <li>You can tag products after uploading</li>
              <li>File names will be used as captions (you can edit later)</li>
              <li>Vertical videos (9:16) work best for reels</li>
            </ul>
          </div>
        </div>
      </main>
    </div>
  );
}
